import React from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { getToothMetadata, getToothSVGPath, TOOTH_CONDITIONS } from './constants/teethData';
import './ToothSVG.css';

/**
 * ToothSVG Component
 * Renders a single tooth as an SVG with interactive features
 * 
 * @param {number} toothNumber - FDI tooth number (11-48 for adults, 51-85 for children)
 * @param {string} condition - Current condition of the tooth (healthy, cavity, filled, etc.)
 * @param {boolean} isSelected - Whether the tooth is currently selected
 * @param {boolean} isHovered - Whether the tooth is currently being hovered
 * @param {function} onClick - Click handler
 * @param {function} onMouseEnter - Mouse enter handler
 * @param {function} onMouseLeave - Mouse leave handler
 * @param {boolean} readOnly - Whether the tooth is in read-only mode (no selection)
 * @param {boolean} showLabel - Whether to show the tooth number label
 * @param {object} customStyle - Additional custom styles
 */
const ToothSVG = ({
    toothNumber,
    condition = 'healthy',
    isSelected = false,
    isHovered = false,
    onClick,
    onMouseEnter,
    onMouseLeave,
    readOnly = false,
    showLabel = true,
    customStyle = {}
}) => {
    const { t } = useTranslation(['tooth', 'common']);
    const metadata = getToothMetadata(toothNumber);
    const svgPaths = getToothSVGPath(toothNumber);
    const conditionData = TOOTH_CONDITIONS[condition] || TOOTH_CONDITIONS.healthy;

    // Determine fill color based on condition
    const fillColor = conditionData.color;
    const borderColor = isSelected
        ? '#1890ff'
        : isHovered
            ? '#1693A5'
            : conditionData.borderColor;

    const strokeWidth = isSelected ? 3 : isHovered ? 2.5 : 2;

    // Handle tooth click
    const handleClick = (e) => {
        if (!readOnly && onClick) {
            e.stopPropagation();
            onClick(toothNumber);
        }
    };

    const handleMouseEnter = (e) => {
        if (!readOnly && onMouseEnter) {
            onMouseEnter(toothNumber);
        }
    };

    const handleMouseLeave = (e) => {
        if (!readOnly && onMouseLeave) {
            onMouseLeave(toothNumber);
        }
    };

    // Determine if tooth is in lower jaw (for rotation)
    const isLowerJaw = toothNumber >= 31 && toothNumber <= 48 ||
        toothNumber >= 71 && toothNumber <= 85;

    // Tooltip content
    const tooltipTitle = (
        <div>
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                {t(`tooth:names.${toothNumber}`, metadata.name)}
            </div>
            <div style={{ fontSize: '12px' }}>
                {t('tooth:number')}: {toothNumber} ({metadata.shortName})
            </div>
            <div style={{ fontSize: '12px', marginTop: 4 }}>
                {t('tooth:condition')}: {t(conditionData.label)}
            </div>
            {condition !== 'healthy' && (
                <div style={{ fontSize: '11px', marginTop: 2, opacity: 0.9 }}>
                    {conditionData.icon}
                </div>
            )}
        </div>
    );

    return (
        <Tooltip title={tooltipTitle} placement="top">
            <div
                className={`tooth-svg-container ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${!readOnly ? 'interactive' : ''}`}
                style={customStyle}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                role={!readOnly ? "button" : "img"}
                tabIndex={!readOnly ? 0 : -1}
                aria-label={`${metadata.name} - ${t(conditionData.label)}`}
                aria-pressed={isSelected}
                onKeyDown={(e) => {
                    if (!readOnly && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleClick(e);
                    }
                }}
            >
                <svg
                    width="45"
                    height="55"
                    viewBox="0 0 30 35"
                    className={`tooth-svg ${isLowerJaw ? 'lower-jaw' : 'upper-jaw'}`}
                    preserveAspectRatio="xMidYMid meet"
                >
                    {/* Gradient Definitions for Realistic Human Teeth Appearance */}
                    <defs>
                        {/* Realistic enamel gradient (slightly creamy white with natural variations) */}
                        <linearGradient id={`enamel-gradient-${toothNumber}`} x1="30%" y1="0%" x2="70%" y2="100%">
                            <stop offset="0%" stopColor={condition === 'healthy' ? '#FFFEF8' : '#ffffff'} stopOpacity="0.98" />
                            <stop offset="25%" stopColor={condition === 'healthy' ? '#FFF9F0' : '#f8f8f8'} stopOpacity="0.95" />
                            <stop offset="50%" stopColor={condition === 'healthy' ? '#FFF5E8' : fillColor} stopOpacity="0.92" />
                            <stop offset="75%" stopColor={condition === 'healthy' ? '#F5EFE3' : fillColor} stopOpacity="0.88" />
                            <stop offset="100%" stopColor={condition === 'healthy' ? '#EDE7DC' : fillColor} stopOpacity="0.85" />
                        </linearGradient>

                        {/* Natural dentin gradient (warm yellowish tones) */}
                        <linearGradient id={`dentin-gradient-${toothNumber}`} x1="25%" y1="15%" x2="75%" y2="85%">
                            <stop offset="0%" stopColor="#FFFCF0" stopOpacity="1" />
                            <stop offset="35%" stopColor="#FFF8E3" stopOpacity="1" />
                            <stop offset="65%" stopColor="#F5E8D0" stopOpacity="0.98" />
                            <stop offset="100%" stopColor="#E8D5B8" stopOpacity="0.95" />
                        </linearGradient>

                        {/* Living pulp gradient (natural pink/red tones) */}
                        <radialGradient id={`pulp-gradient-${toothNumber}`} cx="45%" cy="40%">
                            <stop offset="0%" stopColor="#FFA5A5" stopOpacity="0.95" />
                            <stop offset="40%" stopColor="#FF8585" stopOpacity="0.88" />
                            <stop offset="70%" stopColor="#FF6B6B" stopOpacity="0.82" />
                            <stop offset="100%" stopColor="#F85858" stopOpacity="0.75" />
                        </radialGradient>

                        {/* Root gradient with natural bone-like color */}
                        <linearGradient id={`root-gradient-${toothNumber}`} x1="20%" y1="0%" x2="80%" y2="100%">
                            <stop offset="0%" stopColor="#F8EDD8" stopOpacity="0.85" />
                            <stop offset="50%" stopColor="#E8D8C0" stopOpacity="0.78" />
                            <stop offset="100%" stopColor="#D5C4A8" stopOpacity="0.70" />
                        </linearGradient>

                        {/* Natural enamel shine (subtle, not too glossy) */}
                        <radialGradient id={`highlight-${toothNumber}`} cx="40%" cy="20%">
                            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
                            <stop offset="25%" stopColor="#ffffff" stopOpacity="0.5" />
                            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                        </radialGradient>

                        {/* Realistic tooth texture pattern */}
                        <filter id={`texture-${toothNumber}`}>
                            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" seed="${toothNumber}" result="noise" />
                            <feColorMatrix in="noise" type="saturate" values="0" result="desaturated" />
                            <feComponentTransfer in="desaturated" result="contrast">
                                <feFuncA type="linear" slope="0.03" intercept="0" />
                            </feComponentTransfer>
                            <feBlend in="SourceGraphic" in2="contrast" mode="multiply" />
                        </filter>

                        {/* Soft realistic shadow */}
                        <filter id={`shadow-${toothNumber}`}>
                            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                            <feOffset dx="1.2" dy="2" result="offsetblur" />
                            <feComponentTransfer>
                                <feFuncA type="linear" slope="0.3" />
                            </feComponentTransfer>
                            <feMerge>
                                <feMergeNode />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>

                        {/* Inner shadow for depth and dimension */}
                        <filter id={`inner-shadow-${toothNumber}`}>
                            <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
                            <feOffset dx="0" dy="0.8" result="offsetblur" />
                            <feFlood floodColor="#000000" floodOpacity="0.12" />
                            <feComposite in2="offsetblur" operator="in" />
                            <feMerge>
                                <feMergeNode />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Tooth Root (natural bone-like appearance) */}
                    <path
                        d={svgPaths.root}
                        fill={`url(#root-gradient-${toothNumber})`}
                        stroke="#C8B89B"
                        strokeWidth={strokeWidth * 0.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        opacity="0.75"
                        className="tooth-root"
                    />

                    {/* Dentin Layer (warm inner tooth structure) */}
                    {svgPaths.dentin && (
                        <path
                            d={svgPaths.dentin}
                            fill={`url(#dentin-gradient-${toothNumber})`}
                            stroke="#E8D8C0"
                            strokeWidth={strokeWidth * 0.3}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            opacity="0.92"
                            className="tooth-dentin"
                        />
                    )}

                    {/* Pulp Chamber (living tissue center) */}
                    {svgPaths.pulp && (
                        <path
                            d={svgPaths.pulp}
                            fill={`url(#pulp-gradient-${toothNumber})`}
                            opacity="0.82"
                            className="tooth-pulp"
                            filter={`url(#inner-shadow-${toothNumber})`}
                        />
                    )}

                    {/* Enamel Crown (realistic white outer layer with subtle texture) */}
                    <path
                        d={svgPaths.crown}
                        fill={condition === 'healthy' ? `url(#enamel-gradient-${toothNumber})` : fillColor}
                        stroke={borderColor}
                        strokeWidth={strokeWidth}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        filter={`url(#shadow-${toothNumber})`}
                        className="tooth-crown"
                        opacity={condition === 'healthy' ? 0.96 : 0.88}
                        style={{ filter: `url(#texture-${toothNumber}) url(#shadow-${toothNumber})` }}
                    />

                    {/* Natural enamel shine (subtle highlight) */}
                    <ellipse
                        cx="15"
                        cy="6"
                        rx="6"
                        ry="3.5"
                        fill={`url(#highlight-${toothNumber})`}
                        opacity="0.6"
                        className="tooth-highlight"
                    />

                    {/* Condition indicator icon */}
                    {condition !== 'healthy' && (
                        <text
                            x="15"
                            y="10"
                            textAnchor="middle"
                            fontSize="8"
                            fontWeight="bold"
                            fill="#333"
                            stroke="#fff"
                            strokeWidth="0.5"
                            className="tooth-condition-icon"
                        >
                            {conditionData.icon}
                        </text>
                    )}

                    {/* Selection indicator */}
                    {isSelected && (
                        <g>
                            <circle
                                cx="15"
                                cy="14"
                                r="3"
                                fill="#1890ff"
                                stroke="#fff"
                                strokeWidth="1.2"
                                className="tooth-selection-indicator"
                            />
                            <text
                                x="15"
                                y="16"
                                textAnchor="middle"
                                fontSize="7"
                                fontWeight="bold"
                                fill="#fff"
                            >
                                ✓
                            </text>
                        </g>
                    )}
                </svg>

                {/* Tooth number label */}
                {showLabel && (
                    <div className="tooth-number-label">
                        {toothNumber}
                    </div>
                )}
            </div>
        </Tooltip>
    );
};

export default React.memo(ToothSVG);
